import { getSocialPlatformSettings } from "@/fixtures/socialPlatforms";
import {
  CheckCircleFilled,
  EllipsisOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { Avatar } from "antd";
import { formatDistanceToNow } from "date-fns";
import React, { useMemo } from "react";
import { FaRegComment, FaRegHeart, FaRetweet } from "react-icons/fa";
import { FiBarChart2 } from "react-icons/fi";
import { IoBookmarkOutline } from "react-icons/io5";
import { LuUpload } from "react-icons/lu";
import CaptionExpand from "./components/CaptionExpand";
import MediaSection from "./components/MediaSection";
import { BaseSocialCardProps } from "./types";

export interface TwitterCardProps extends BaseSocialCardProps {
  platformId: string;
}

const TwitterCard: React.FC<TwitterCardProps> = ({
  caption,
  mediaUrls = [],
  username = "User Name",
  profileUrl,
  postedAt,
  platformId,
  isTemplateVideo,
}) => {
  const characterLimit = useMemo(() => {
    const settings = getSocialPlatformSettings(platformId);
    return settings?.limits?.caption;
  }, [platformId]);

  const displayCaption = useMemo(() => {
    if (characterLimit && caption.length > characterLimit) {
      return caption.substring(0, characterLimit) + "...";
    }
    return caption;
  }, [caption, characterLimit]);

  return (
    <div className="w-full overflow-hidden py-6 px-4 bg-antd-colorBgContainer text-antd-colorText twitter-card-preview">
      <style>{`
        .twitter-card-preview .hashtag {
          color: #0095F6; /* Using Instagram's Blue */
          text-decoration: none;
        }
        .twitter-card-preview .hashtag:hover {
          text-decoration: underline;
        }
        /* Links (.caption-link) remain default text color */
      `}</style>
      <div className="flex items-start">
        <Avatar
          size={40}
          shape="circle"
          src={profileUrl}
          icon={!profileUrl ? <UserOutlined /> : undefined}
          className="mr-3 mt-0.5 flex-shrink-0"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center flex-wrap flex-1 min-w-0 mr-2">
              <span className="font-bold text-[15px] mr-1 truncate">
                {username}
              </span>
              {username === "Alex Forbes" && (
                <span className="text-blue-500 mr-1 flex-shrink-0">
                  <CheckCircleFilled className="h-[18px] w-[18px] inline-flex" />
                </span>
              )}
              <span className="text-antd-colorTextSecondary text-[15px] mr-1 truncate">
                @{username.replace(/\s+/g, "").toLowerCase()}
              </span>
              <span className="text-antd-colorTextSecondary text-[15px] mr-1 flex-shrink-0">
                •
              </span>
              <span className="text-antd-colorTextSecondary text-[15px] whitespace-nowrap flex-shrink-0">
                {postedAt
                  ? formatDistanceToNow(new Date(postedAt), {
                      addSuffix: false,
                    })
                  : "Now"}
              </span>
            </div>

            <div className="flex-shrink-0">
              <div className="text-antd-colorTextSecondary">
                <EllipsisOutlined />
              </div>
            </div>
          </div>

          <div>
            {displayCaption && (
              <div className="mt-2 mb-1">
                <CaptionExpand
                  text={displayCaption}
                  initialLines={4}
                  showMoreText="Show more"
                  showLessText="...less"
                  linkColor="blue"
                  className="text-sm text-antd-colorText"
                />
              </div>
            )}

            <div className="rounded-xl overflow-hidden border border-antd-colorBorderSecondary mb-3 mt-1 relative flex min-h-[200px]">
              <MediaSection
                mediaUrls={mediaUrls}
                isTemplateVideo={isTemplateVideo}
              />
            </div>

            <div className="flex items-center justify-between text-antd-colorTextSecondary text-sm mt-3">
              <div className="flex items-center gap-1">
                <FaRegComment />
                <span>379</span>
              </div>
              <div className="flex items-center gap-1">
                <FaRetweet />
                <span>6.8K</span>
              </div>
              <div className="flex items-center gap-1">
                <FaRegHeart />
                <span>12K</span>
              </div>
              <div className="flex items-center gap-1">
                <FiBarChart2 />
                <span>1.8M</span>
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center">
                  <IoBookmarkOutline />
                </div>
                <div className="flex items-center">
                  <LuUpload />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TwitterCard;
